<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'

import type {
  WaitingRoomItemOption,
  WaitingRoomItemOptionsResponse,
} from '@/api/fileManagement'
import { useSnackbarStore } from '@/store/snackbar'

type LoadOptionsParams = {
  search: string
  page: number
  per_page: number
}

interface Props {
  modelValue: boolean
  initialFilename?: string | null
  extension?: string | null
  loading?: boolean
  requestKey?: string | number | null
  loadInitialOptionsWithoutSearch?: boolean
  loadOptions: (params: LoadOptionsParams) => Promise<{ data: WaitingRoomItemOptionsResponse }>
}

const props = withDefaults(defineProps<Props>(), {
  initialFilename: '',
  extension: '',
  loading: false,
  requestKey: null,
  loadInitialOptionsWithoutSearch: false,
})

interface Emit {
  (e: 'update:modelValue', value: boolean): void
  (e: 'save', value: string): void
}

const emit = defineEmits<Emit>()
const snackbarStore = useSnackbarStore()
const newFilename = ref('')
const identitySearch = ref('')
const loadedIdentitySearch = ref('')
const identityOptions = ref<WaitingRoomItemOption[]>([])
const identityOptionsLoading = ref(false)
const identityOptionsPage = ref(1)
const hasMoreIdentityOptions = ref(false)
const identityMenuOpen = ref(false)
const identityOptionsPerPage = 25
let latestIdentityRequest = 0

const isVisible = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

const normalizedFilename = computed(() => newFilename.value.trim())

const hasIdentitySearch = computed(() => !!identitySearch.value.trim())

const showIdentitySearchStatus = computed(() => {
  return hasIdentitySearch.value && (identityOptionsLoading.value || !identityOptions.value.length)
})

const resetIdentityOptions = () => {
  latestIdentityRequest += 1
  identityOptions.value = []
  loadedIdentitySearch.value = ''
  identityOptionsPage.value = 1
  hasMoreIdentityOptions.value = false
  identityOptionsLoading.value = false
}

const prepareIdentityOptionsSearch = () => {
  latestIdentityRequest += 1
  identityOptions.value = []
  identityOptionsPage.value = 1
  hasMoreIdentityOptions.value = false
  identityOptionsLoading.value = true
}

const openIdentityMenu = () => {
  if (!props.modelValue || props.loading) return
  identityMenuOpen.value = true
  void nextTick(() => {
    if (props.modelValue && !props.loading) {
      identityMenuOpen.value = true
    }
  })
  window.setTimeout(() => {
    if (props.modelValue && !props.loading) {
      identityMenuOpen.value = true
    }
  }, 0)
}

const loadIdentityOptions = async (search: string, page = 1) => {
  const normalizedSearch = (search || '').trim()
  if (!props.modelValue || (!normalizedSearch && !props.loadInitialOptionsWithoutSearch)) {
    resetIdentityOptions()
    return
  }

  const requestId = ++latestIdentityRequest
  identityOptionsLoading.value = true
  try {
    const res = await props.loadOptions({
      search: normalizedSearch,
      page,
      per_page: identityOptionsPerPage,
    })
    if (requestId !== latestIdentityRequest) return

    const nextOptions = res.data.results || []
    loadedIdentitySearch.value = normalizedSearch
    if (page === 1) {
      identityOptions.value = nextOptions
    } else {
      const existingIdentities = new Set(identityOptions.value.map(option => option.identity))
      identityOptions.value = [
        ...identityOptions.value,
        ...nextOptions.filter(option => !existingIdentities.has(option.identity)),
      ]
    }
    identityOptionsPage.value = page
    hasMoreIdentityOptions.value = Boolean(res.data.next)
    openIdentityMenu()
  } catch (error: any) {
    if (requestId !== latestIdentityRequest) return
    identityOptions.value = []
    hasMoreIdentityOptions.value = false
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Identity Search Failed',
      text: error?.response?.data?.detail || error?.message || 'Unable to search item identities.',
    })
  } finally {
    if (requestId === latestIdentityRequest) {
      identityOptionsLoading.value = false
    }
  }
}

const searchIdentityOptions = useDebounceFn((search: string) => {
  void loadIdentityOptions(search, 1)
}, 300)

const onIdentitySearchUpdate = (value: string | null) => {
  const search = value || ''
  identitySearch.value = search
  newFilename.value = search
  openIdentityMenu()

  if (!search.trim()) {
    if (!props.loadInitialOptionsWithoutSearch) {
      resetIdentityOptions()
      return
    }
    prepareIdentityOptionsSearch()
    searchIdentityOptions('')
    return
  }
  prepareIdentityOptionsSearch()
  searchIdentityOptions(search)
}

const onIdentitySelected = (value: unknown) => {
  const selectedOption = value && typeof value === 'object'
    ? value as WaitingRoomItemOption
    : null
  const identity = typeof value === 'string'
    ? value
    : selectedOption?.identity || ''
  newFilename.value = identity
  identitySearch.value = identity
}

const loadMoreIdentityOptions = () => {
  openIdentityMenu()
  if (identityOptionsLoading.value || !hasMoreIdentityOptions.value) return
  void loadIdentityOptions(loadedIdentitySearch.value, identityOptionsPage.value + 1)
}

watch(
  () => [props.modelValue, props.initialFilename, props.requestKey],
  ([visible]) => {
    if (visible) {
      newFilename.value = props.initialFilename || ''
      identitySearch.value = newFilename.value
      resetIdentityOptions()
      openIdentityMenu()
      void loadIdentityOptions(
        props.loadInitialOptionsWithoutSearch ? '' : identitySearch.value,
        1,
      )
    } else {
      identityMenuOpen.value = false
      resetIdentityOptions()
    }
  },
  { immediate: true },
)

const save = () => {
  if (!normalizedFilename.value || props.loading) return
  emit('save', normalizedFilename.value)
}
</script>

<template>
  <VDialog
    v-model="isVisible"
    width="520"
    max-width="95vw"
  >
    <VCard>
      <VCardTitle class="d-flex align-center justify-space-between">
        <span>Rename New File Name</span>
        <VBtn
          icon="tabler-x"
          variant="text"
          :disabled="loading"
          @click="isVisible = false"
        />
      </VCardTitle>

      <VDivider />

      <VCardText class="pt-5">
        <VCombobox
          :model-value="newFilename"
          :search="identitySearch"
          v-model:menu="identityMenuOpen"
          :items="identityOptions"
          item-title="identity"
          item-value="id"
          return-object
          label="New File Name"
          autofocus
          counter="256"
          maxlength="288"
          clearable
          no-filter
          :loading="identityOptionsLoading"
          :disabled="loading"
          @focusin="openIdentityMenu"
          @focus="openIdentityMenu"
          @click="openIdentityMenu"
          @click:control="openIdentityMenu"
          @mousedown:control="openIdentityMenu"
          @keydown="openIdentityMenu"
          @update:modelValue="onIdentitySelected"
          @update:search="onIdentitySearchUpdate"
        >
          <template #no-data>
            <div class="px-4 py-3 text-medium-emphasis">
              {{
                identityOptionsLoading
                  ? 'Searching...'
                  : identitySearch.trim()
                    ? 'Items with this name do not exist in our database.'
                    : loadInitialOptionsWithoutSearch
                      ? 'No items available in this scope.'
                      : 'Type to search names.'
              }}
            </div>
          </template>

          <template
            v-if="showIdentitySearchStatus || hasMoreIdentityOptions"
            #append-item
          >
            <template v-if="showIdentitySearchStatus">
              <VDivider />
              <VListItem disabled>
                <template #prepend>
                  <VProgressCircular
                    v-if="identityOptionsLoading"
                    indeterminate
                    size="18"
                    width="2"
                  />
                  <VIcon
                    v-else
                    icon="tabler-info-circle"
                    size="18"
                  />
                </template>
                <VListItemTitle>
                  {{
                    identityOptionsLoading
                      ? 'Searching...'
                      : 'No matches found in this scope.'
                  }}
                </VListItemTitle>
              </VListItem>
            </template>
            <VDivider v-if="hasMoreIdentityOptions" />
            <VListItem
              v-if="hasMoreIdentityOptions"
              class="cursor-pointer"
              :disabled="identityOptionsLoading"
              @click="loadMoreIdentityOptions"
            >
              <template #prepend>
                <VProgressCircular
                  v-if="identityOptionsLoading"
                  indeterminate
                  size="18"
                  width="2"
                />
                <VIcon
                  v-else
                  icon="tabler-chevron-down"
                  size="18"
                />
              </template>
              <VListItemTitle>Load more...</VListItemTitle>
            </VListItem>
          </template>

          <template #append-inner>
            <span class="text-medium-emphasis">{{ extension || '' }}</span>
          </template>
        </VCombobox>
      </VCardText>

      <VCardActions class="justify-end px-6 pb-5">
        <VBtn
          variant="text"
          :disabled="loading"
          @click="isVisible = false"
        >
          Cancel
        </VBtn>
        <VBtn
          color="primary"
          prepend-icon="tabler-device-floppy"
          :loading="loading"
          :disabled="!normalizedFilename"
          @click="save"
        >
          Save
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
